import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { Draggable } from '@hello-pangea/dnd';
import styles from './KanbanCardV2.module.scss';
import CardTagsList from './components/CardTagsList';

/**
 * 🎨 KanbanCardV2 - Novo design moderno para cards do Kanban
 * 
 * Layout inspirado em job boards modernos:
 * - Avatar no topo
 * - Nome do produto
 * - Nome do cliente
 * - Tags coloridas
 * - Status badge
 * - Data de atualização
 */

// KAN-13: colunas onde "Cancelar envio" faz sentido (não em ENTREGUE/PAGOS/FRUSTRADOS).
const CANCELLABLE_COLUMNS = new Set([
  'expedicao', 'enviados', 'saiu_entrega', 'retirar_correios', 'requer_atencao',
]);

const KanbanCardV2 = ({ card, index, enableDrag = true, onCardClick, onManageTags, onCancelShipment, onReport, columnId }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const avatarRef = React.useRef(null);

  // Funções para controlar o tooltip do avatar
  const handleMouseEnter = () => {
    if (avatarRef.current) {
      const rect = avatarRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top - 40,
        left: rect.left + rect.width / 2
      });
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  // Função para obter cor do status badge
  // KAN-08: aceita os novos labels (Em processamento/Em expedição) + legacy (Em expedição/Enviado)
  const getStatusBadgeClass = (status) => {
    const statusClassMap = {
      'Reportado': 'reportado',
      'Em processamento': 'expedicao',  // novo: classe CSS expedicao mantida
      'Em expedição': 'enviado',         // novo: classe CSS enviado mantida
      'Enviado': 'enviado',              // legacy fallback
      'Saiu para entrega': 'saiuEntrega',
      'Aguardando retirada': 'retirada',
      'Requer atenção': 'atencao',
      'Entregue': 'entregue',
      'Inadimplente': 'inadimplente',
      'Frustrado': 'frustrado',
      'Pago': 'pago'
    };
    return statusClassMap[status] || 'default';
  };

  // Função para obter status detalhado para frustrados
  const getDetailedStatus = (card) => {
    if (card.status === 'Frustrado') {
      // Priorizar frustration_column do metadata (quando movido no Kanban de Frustrações)
      if (card.metadata?.frustration_column) {
        const columnMap = {
          'roubos': 'Roubo',
          'devolvidos-reverso': 'Devolvido',
          'retornado': 'Retornado',
          'cancelado-sem-custo': 'Cancelado',
          'frustracoes': 'Entregue',
          // KAN-09 / Pacote 5: nova coluna "Solicitações Pendentes"
          'pending_requests': 'Solicitação'
        };
        return columnMap[card.metadata.frustration_column] || 'Frustrado';
      }
      
      // Fallback para sale_status
      if (card.saleStatus) {
        const statusMap = {
          'refunded': 'Devolvido',
          'cancelled': 'Cancelado', 
          'stolen': 'Roubo',
          'lost': 'Perdido',
          'damaged': 'Danificado',
          'returned': 'Retornado',
          'pending_payment': 'Entregue',
          'delivered': 'Entregue',
          'processing': 'Processando',
          'shipped': 'Enviado',
          'paid': 'Pago'
        };
        return statusMap[card.saleStatus] || card.saleStatus;
      }
    }
    return card.status;
  };

  // KAN-13: botão "Cancelar envio" só nas colunas operacionais (não em
  // ENTREGUE/PAGOS/FRUSTRADOS). E só pra cards de sale_item (têm saleItemId).
  const canCancelShipment = !!card.saleItemId
    && CANCELLABLE_COLUMNS.has(columnId)
    && card.itemStatus !== 'cancelled'
    && card.itemStatus !== 'delivered'
    && card.itemStatus !== 'refunded';

  // KAN-12 / Pacote 5: botão "Reportar pedido" aparece nas mesmas colunas que
  // "Cancelar envio" (operacionais, aba Geral). Move o card pra coluna escolhida
  // na aba Frustrações.
  const canReport = !!card.saleItemId
    && CANCELLABLE_COLUMNS.has(columnId)
    && card.itemStatus !== 'cancelled'
    && card.itemStatus !== 'delivered'
    && card.itemStatus !== 'refunded';

  // KAN-17: cor de borda colorida pra cards de venda compartilhada
  const sharedBorderStyle = card.kan17?.isShared
    ? {
        borderLeft: `4px solid ${card.kan17.borderColor}`,
        paddingLeft: 8,
      }
    : {};

  const cardContent = (
    <div className={styles.card} style={sharedBorderStyle}>
      {/* KAN-17: badge "Venda #X · n/N" quando faz parte de venda compartilhada */}
      {card.kan17?.isShared && (
        <div
          title={card.kan17.tooltip}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            background: card.kan17.borderColor,
            color: '#fff',
            fontSize: 10,
            fontWeight: 700,
            padding: '2px 8px',
            borderRadius: 4,
            marginBottom: 6,
            letterSpacing: 0.3,
          }}
        >
          <Icon icon="mdi:package-variant-closed-plus" width="11" />
          {card.kan17.badge}
        </div>
      )}

      {/* KAN-07: badge "Disponível há X dia(s)" — só em RETIRAR NOS CORREIOS */}
      {card.kan07?.pickupDays != null && (
        <div
          title="Tempo aguardando retirada na agência dos Correios"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            background: card.kan07.alertLevel === 'alert' ? '#fef2f2' : '#f3f4f6',
            color: card.kan07.alertLevel === 'alert' ? '#991b1b' : '#374151',
            border: `1px solid ${card.kan07.alertLevel === 'alert' ? '#fecaca' : '#e5e7eb'}`,
            fontSize: 11,
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: 12,
            marginBottom: 6,
            marginLeft: card.kan17?.isShared ? 6 : 0,
          }}
        >
          <Icon
            icon={card.kan07.alertLevel === 'alert' ? 'mdi:clock-alert-outline' : 'mdi:clock-outline'}
            width="12"
          />
          {card.kan07.pickupDays === 0
            ? 'Disponível hoje'
            : `Disponível há ${card.kan07.pickupDays} dia${card.kan07.pickupDays > 1 ? 's' : ''}`}
        </div>
      )}

      {/* HEADER: Avatar + Actions */}
      <div className={styles.cardHeader}>
        <div
          ref={avatarRef}
          className={styles.avatarContainer}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {card.avatar ? (
            <img
              src={card.avatar}
              alt={card.name}
              className={styles.avatar}
            />
          ) : (
            <div className={styles.avatarFallback}>
              {card.name ? card.name.charAt(0).toUpperCase() : '?'}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className={styles.cardActions}>
          {/* KAN-12 / Pacote 5: Reportar pedido (mesmas colunas do Cancelar envio) */}
          {canReport && (
            <button
              className={styles.tagsButton}
              onClick={(e) => {
                e.stopPropagation();
                onReport && onReport(card);
              }}
              title="Reportar pedido"
              style={{ color: '#ea580c' }}
            >
              <Icon icon="mdi:alert-octagon-outline" />
            </button>
          )}
          {/* KAN-13: Cancelar envio (só em colunas operacionais) */}
          {canCancelShipment && (
            <button
              className={styles.tagsButton}
              onClick={(e) => {
                e.stopPropagation();
                onCancelShipment && onCancelShipment(card);
              }}
              title="Cancelar envio"
              style={{ color: '#dc2626' }}
            >
              <Icon icon="mdi:truck-remove-outline" />
            </button>
          )}
          {/* Ícone de Tags */}
          <button
            className={styles.tagsButton}
            onClick={(e) => {
              e.stopPropagation();
              onManageTags && onManageTags(card);
            }}
            title="Gerenciar tags"
          >
            <Icon icon="mdi:tag-multiple" />
          </button>
        </div>
      </div>

      {/* PRODUCT NAME */}
      <div className={styles.productName}>
        {card.description}
      </div>

      {/* CUSTOMER NAME */}
      <div className={styles.customerName}>
        {card.name}
      </div>

      {/* TAGS */}
      <CardTagsList
        tags={card.tags || []}
        maxVisible={4}
      />

      {/* FOOTER: Data + Status */}
      <div className={styles.cardFooter}>
        <div className={styles.footerLeft}>
          <Icon icon="mdi:update" className={styles.footerIcon} />
          <span className={styles.footerText}>
            {card.date}
          </span>
        </div>
        <div className={styles.footerRight}>
          <span className={`${styles.statusBadge} ${styles[getStatusBadgeClass(card.status)]}`}>
            {getDetailedStatus(card)}
          </span>
        </div>
      </div>
    </div>
  );

  // Tooltip do avatar
  const avatarTooltip = showTooltip && (
    <div 
      className={styles.avatarTooltip}
      style={{
        position: 'fixed',
        top: tooltipPosition.top,
        left: tooltipPosition.left,
        transform: 'translateX(-50%)',
        zIndex: 9999
      }}
    >
      {card.name}
    </div>
  );

  // Renderização sem drag
  if (!enableDrag) {
    return (
      <>
        <div
          className={styles.cardWrapper}
          onClick={() => onCardClick && onCardClick(card)}
        >
          {cardContent}
        </div>
        {avatarTooltip}
      </>
    );
  }

  // Renderização com drag
  return (
    <>
      <Draggable draggableId={String(card.id)} index={index}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className={`${styles.cardWrapper} ${snapshot.isDragging ? styles.dragging : ''}`}
            onClick={() => onCardClick && onCardClick(card)}
          >
            {cardContent}
          </div>
        )}
      </Draggable>
      {avatarTooltip}
    </>
  );
};

export default KanbanCardV2;
